import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Building2, Users, TrendingUp, ChevronRight, Briefcase, Calendar, ChevronDown, Phone, Mail, Target, Shield, ArrowRight, Linkedin, Instagram, Youtube, Zap, BookOpen, BarChart3, GraduationCap } from "lucide-react";
import { toast } from 'sonner';

const CorporateCooperation = () => {
    const [formState, setFormState] = useState({
        company: '',
        role: '',
        topic: '',
        email: '',
        phone: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const normalizePhone = (value) => String(value || '').replace(/\s+/g, '');

    const scrollToContact = (topic) => {
        if (topic) setFormState(prev => ({ ...prev, topic }));
        const element = document.getElementById('contact');
        if (element) {
            const offset = 80;
            const bodyRect = document.body.getBoundingClientRect().top;
            const elementRect = element.getBoundingClientRect().top;
            const elementPosition = elementRect - bodyRect;
            const offsetPosition = elementPosition - offset;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const response = await fetch('/api/ecomail-corporate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    company: formState.company,
                    role: formState.role,
                    topic: formState.topic,
                    email: formState.email,
                    phone: normalizePhone(formState.phone),
                }),
            });

            if (!response.ok) {
                const errorBody = await response.json().catch(() => ({}));
                throw new Error(errorBody?.error || 'Chyba pri odosielaní.');
            }

            setIsSubmitted(true);
        } catch (error) {
            toast.error(error.message || 'Chyba pri odosielaní. Skúste to neskôr.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleChange = (e) => {
        setFormState({
            ...formState,
            [e.target.name]: e.target.value
        });
    };

    return (
        <div className="min-h-screen bg-background font-body text-brand-dark-blue overflow-x-hidden">
            {/* 1. HERO SECTION */}
            <section className="relative min-h-[90vh] flex items-center justify-center overflow-x-clip bg-brand-dark-blue border-b border-brand-gray/10 text-white">
                <div
                    className="absolute inset-0 opacity-20 pointer-events-none"
                    style={{ backgroundImage: 'url(/spolupraca_hero.jpg)', backgroundSize: 'cover', backgroundPosition: 'center' }}
                />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-brand-dark-blue/80 via-brand-dark-blue/95 to-brand-dark-blue" />
                <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand-orange/10 rounded-full blur-[120px] animate-pulse" />

                <div className="container px-4 md:px-6 relative z-10 max-w-full min-w-0">
                    <div className="flex flex-col items-center text-center space-y-6 sm:space-y-8 max-w-5xl mx-auto min-w-0 px-1">
                        <div className="inline-block px-3 sm:px-4 py-1.5 rounded-full bg-brand-orange/10 border border-brand-orange/20 text-brand-orange text-xs sm:text-sm font-semibold tracking-wide uppercase mb-2 backdrop-blur-sm max-w-full">
                            Business Solutions
                        </div>

                        <h1 className="font-heading text-3xl min-[400px]:text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.1] break-words w-full max-w-full">
                            Systém pre finančne zdravú firmu<br />
                            <span className="text-brand-orange">& lojálnych zamestnancov</span>
                        </h1>

                        <p className="text-base sm:text-xl md:text-2xl text-gray-200 max-w-4xl mx-auto leading-relaxed font-light px-1">
                            Finančne gramotný zamestnanec chápe hodnotu benefitov, nie je pod tlakom a buduje firmu s vami. Pomáham majiteľom chrániť aktíva a zamestnancom budovať ich vlastnú rentu.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-5 pt-8">
                            <button
                                className="inline-flex items-center justify-center gap-2 bg-brand-orange hover:bg-red-600 text-white text-lg font-bold py-5 px-10 rounded-2xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl shadow-lg border-0"
                                onClick={() => scrollToContact()}
                            >
                                <span>Nezáväzná konzultácia pre firmy</span>
                                <ChevronRight className="w-5 h-5" />
                            </button>
                            <button
                                className="inline-flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 backdrop-blur-sm border border-white/20 text-white text-lg font-semibold py-5 px-10 rounded-2xl transition-all duration-300"
                                onClick={() => scrollToContact('benefits')}
                            >
                                Chcem audit benefitov
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* 2. THE THREE PILLARS */}
            <section className="py-24 bg-white relative overflow-hidden">
                <div className="container px-4 md:px-6 relative z-10">
                    <div className="max-w-4xl mb-2 lg:mb-4">
                        <h2 className="font-heading text-4xl lg:text-6xl font-bold text-brand-dark-blue mb-4">
                            Tri piliere, ktoré <span className="text-brand-orange">menia firmu</span>
                        </h2>
                        <p className="text-xl text-gray-600 leading-relaxed">
                            Nerečníme na poradách. Prinášame konkrétne nástroje, ktoré zvyšujú ROI vašich investícií do ľudí a chránia to, čo ste vybudovali.
                        </p>
                    </div>

                    <div className="space-y-16 lg:space-y-24">
                        {/* PILLAR 1: EDUCATION */}
                        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
                            <div className="space-y-6 order-2 lg:order-1">
                                <div className="inline-flex items-center gap-3 text-brand-orange font-bold uppercase tracking-widest text-sm">
                                    <div className="w-12 h-1 bg-brand-orange"></div>
                                    Pilier 01
                                </div>
                                <h3 className="font-heading text-4xl font-bold text-brand-dark-blue leading-tight">
                                    Vzdelávanie & Workshopy
                                </h3>
                                <p className="text-xl text-gray-600">
                                    Zamestnanec, ktorý nie je v strese z peňazí a chápe, čo mu firma dáva, je o <span className="text-brand-dark-blue font-bold">30% lojálnejší</span>.
                                </p>

                                <div className="grid sm:grid-cols-2 gap-6">
                                    <div className="space-y-3 p-5 bg-blue-50/30 rounded-2xl border border-blue-100/50">
                                        <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center text-brand-dark-blue">
                                            <Users size={20} />
                                        </div>
                                        <h4 className="font-bold">Pre celú firmu</h4>
                                        <p className="text-gray-600 text-sm leading-relaxed">Poldenné prednášky motivujúce k finančnej zodpovednosti.</p>
                                    </div>
                                    <div className="space-y-3 p-5 bg-orange-50/30 rounded-2xl border border-orange-100/50">
                                        <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center text-brand-orange">
                                            <Zap size={20} />
                                        </div>
                                        <h4 className="font-bold">Pre Management</h4>
                                        <p className="text-gray-600 text-sm leading-relaxed">Hard-skills workshopy o budovaní renty a majetku.</p>
                                    </div>
                                </div>

                                <div className="mt-8">
                                    <button
                                        onClick={() => scrollToContact('education')}
                                        className="inline-flex items-center gap-2 bg-brand-dark-blue text-white font-bold py-4 px-8 rounded-xl hover:bg-brand-orange transition-all group"
                                    >
                                        Mám záujem o workshop
                                        <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                                    </button>
                                </div>
                            </div>
                            <div className="relative order-1 lg:order-2">
                                <img src="/spolupraca_hero.jpeg" className="rounded-3xl shadow-2xl" alt="Education" />
                                <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-xl border border-gray-100 hidden md:block max-w-[280px]">
                                    <p className="text-sm font-medium italic">„Simulácia 30 rokov finančného života“ — učíme, ako reálne premýšľať o majetku.</p>
                                </div>
                            </div>
                        </div>

                        {/* PILLAR 2: BUSINESS GROWTH */}
                        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
                            <div className="relative lg:order-1 order-1">
                                <img src="/spolupraca_cta.jpeg" className="rounded-3xl shadow-2xl" alt="Business Growth" />
                            </div>
                            <div className="space-y-6 lg:order-2 order-2">
                                <div className="inline-flex items-center gap-3 text-brand-orange font-bold uppercase tracking-widest text-sm">
                                    <div className="w-12 h-1 bg-brand-orange"></div>
                                    Pilier 02
                                </div>
                                <h3 className="font-heading text-4xl font-bold text-brand-dark-blue leading-tight">
                                    Financovanie & Rozvoj
                                </h3>
                                <p className="text-xl text-gray-600">
                                    Pripravujeme firmu na to, aby bola silná v očiach bánk a chránená pred nepredvídateľnými rizikami.
                                </p>

                                <ul className="space-y-4">
                                    <li className="flex gap-4 p-4 rounded-2xl bg-gray-50 border border-gray-100">
                                        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center text-green-600 shrink-0">
                                            <TrendingUp size={24} />
                                        </div>
                                        <div>
                                            <h4 className="font-bold">Bankability & Financovanie</h4>
                                            <p className="text-gray-600 text-sm">Úvery, leasingy a kapitál. Pripravíme vás tak, aby vám banka nevedela povedať nie.</p>
                                        </div>
                                    </li>
                                    <li className="flex gap-4 p-4 rounded-2xl bg-gray-50 border border-gray-100">
                                        <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-brand-dark-blue shrink-0">
                                            <Shield size={24} />
                                        </div>
                                        <div>
                                            <h4 className="font-bold">Ochrana rizík & Majetku</h4>
                                            <p className="text-gray-600 text-sm">Dôsledné oddelenie firemných a osobných rizík pre pokojný spánok majiteľa.</p>
                                        </div>
                                    </li>
                                </ul>

                                <div className="mt-8">
                                    <button
                                        onClick={() => scrollToContact('financing')}
                                        className="inline-flex items-center gap-2 bg-brand-dark-blue text-white font-bold py-4 px-8 rounded-xl hover:bg-brand-orange transition-all group"
                                    >
                                        Zistiť možnosti financovania
                                        <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* PILLAR 3: BENEFITS */}
                        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
                            <div className="space-y-6 order-2 lg:order-1">
                                <div className="inline-flex items-center gap-3 text-brand-orange font-bold uppercase tracking-widest text-sm">
                                    <div className="w-12 h-1 bg-brand-orange"></div>
                                    Pilier 03
                                </div>
                                <h3 className="font-heading text-4xl font-bold text-brand-dark-blue leading-tight">
                                    Funkčné Benefity
                                </h3>
                                <p className="text-xl text-gray-600">
                                    Zastavte zbytočné výdavky na benefity, ktoré nikto neocení. Nastavíme systém, ktorý ľudia reálne využijú.
                                </p>

                                <div className="grid sm:grid-cols-2 gap-4">
                                    <div className="p-5 bg-gray-50 rounded-2xl border border-gray-100">
                                        <h4 className="font-bold text-base mb-1">3. pilier (DDS)</h4>
                                        <p className="text-gray-600 text-xs">Nastavenie a správa príspevkov s max. efektivitou.</p>
                                    </div>
                                    <div className="p-5 bg-gray-50 rounded-2xl border border-gray-100">
                                        <h4 className="font-bold text-base mb-1">Skupinové poistenie</h4>
                                        <p className="text-gray-600 text-xs">Kolektívne krytie ako stabilizačný prvok tímu.</p>
                                    </div>
                                    <div className="p-5 bg-gray-50 rounded-2xl border border-gray-100">
                                        <h4 className="font-bold text-base mb-1">Exkluzívne zľavy</h4>
                                        <p className="text-gray-600 text-xs">Lepšie podmienky finančných produktov pre váš tím.</p>
                                    </div>
                                    <div className="p-5 bg-brand-orange/5 border border-brand-orange/20 rounded-2xl">
                                        <div className="flex items-center gap-2 mb-1">
                                            <h4 className="font-bold text-brand-orange text-base">Edu Subscription</h4>
                                        </div>
                                        <p className="text-gray-600 text-xs">Digitálne vzdelávanie pre každého zamestnanca.</p>
                                    </div>
                                </div>

                                <div className="mt-8">
                                    <button
                                        onClick={() => scrollToContact('benefits')}
                                        className="inline-flex items-center gap-2 bg-brand-dark-blue text-white font-bold py-4 px-8 rounded-xl hover:bg-brand-orange transition-all group"
                                    >
                                        Chcem audit benefitov
                                        <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                                    </button>
                                </div>
                            </div>
                            <div className="relative order-1 lg:order-2">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-4">
                                        <img src="/book_4.jpeg" className="rounded-2xl shadow-lg w-full h-[200px] object-cover" alt="B1" />
                                        <img src="/book_5.jpeg" className="rounded-2xl shadow-lg w-full h-[300px] object-cover" alt="B2" />
                                    </div>
                                    <div className="space-y-4 pt-12">
                                        <img src="/book_6.jpeg" className="rounded-2xl shadow-lg w-full h-[300px] object-cover" alt="B3" />
                                        <img src="/book_7.jpeg" className="rounded-2xl shadow-lg w-full h-[200px] object-cover" alt="B4" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 3. CONTACT FORM */}
            <section id="contact" className="py-24 bg-brand-dark-blue relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-brand-orange/10 to-transparent pointer-events-none" />

                <div className="container px-4 max-w-6xl mx-auto">
                    {!isSubmitted ? (
                        <div className="bg-white rounded-3xl p-8 md:p-12 lg:p-16 shadow-2xl relative z-10">
                            <div className="grid lg:grid-cols-5 gap-12 items-start">
                                <div className="lg:col-span-2 space-y-8">
                                    <div>
                                        <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4 leading-tight">
                                            Poďme budovať hodnotu vo vašej firme.
                                        </h2>
                                        <p className="text-lg text-gray-600">
                                            Peťo sa ozve osobne. Krátko si zavoláte a zistíte, či dokáže posunúť vašu firmu a ľudí na novú úroveň.
                                        </p>
                                    </div>
                                    <div className="space-y-6">
                                        <div className="text-sm text-gray-500 font-medium uppercase tracking-wide">Sledujte Peťa</div>
                                        <div className="flex gap-4">
                                            <a href="https://www.linkedin.com/in/petosloboda/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-brand-orange/10 flex items-center justify-center text-brand-orange hover:bg-brand-orange hover:text-white transition-all">
                                                <Linkedin className="w-6 h-6" />
                                            </a>
                                            <a href="https://www.instagram.com/petosloboda/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-brand-orange/10 flex items-center justify-center text-brand-orange hover:bg-brand-orange hover:text-white transition-all">
                                                <Instagram className="w-6 h-6" />
                                            </a>
                                            <a href="https://www.youtube.com/@peto.sloboda" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-brand-orange/10 flex items-center justify-center text-brand-orange hover:bg-brand-orange hover:text-white transition-all">
                                                <Youtube className="w-6 h-6" />
                                            </a>
                                        </div>
                                    </div>
                                </div>

                                <div className="lg:col-span-3 bg-gray-50 p-8 rounded-2xl border border-gray-100">
                                    <form onSubmit={handleSubmit} className="space-y-6">
                                        <div className="grid md:grid-cols-2 gap-6">
                                            <div className="space-y-2">
                                                <label className="text-sm font-semibold text-gray-700">Názov firmy</label>
                                                <div className="relative">
                                                    <Building2 className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                                                    <input
                                                        type="text"
                                                        name="company"
                                                        required
                                                        className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20 outline-none transition-all"
                                                        placeholder="Vaša spoločnosť"
                                                        value={formState.company}
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-sm font-semibold text-gray-700">Vaša rola</label>
                                                <div className="relative">
                                                    <Briefcase className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                                                    <input
                                                        type="text"
                                                        name="role"
                                                        required
                                                        className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20 outline-none transition-all"
                                                        placeholder="napr. CEO, HR"
                                                        value={formState.role}
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-sm font-semibold text-gray-700">Téma záujmu</label>
                                            <div className="relative">
                                                <ChevronDown className="absolute right-3 top-3.5 w-5 h-5 text-gray-400 pointer-events-none" />
                                                <select
                                                    name="topic"
                                                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20 outline-none transition-all appearance-none bg-white font-medium text-gray-600"
                                                    value={formState.topic}
                                                    onChange={handleChange}
                                                >
                                                    <option value="">Vyberte možnosť...</option>
                                                    <option value="education">Vzdelávanie / Workshopy</option>
                                                    <option value="financing">Bankability & Financovanie</option>
                                                    <option value="benefits">Audit benefitov / DDS</option>
                                                    <option value="other">Iné</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div className="grid md:grid-cols-2 gap-6">
                                            <div className="space-y-2">
                                                <label className="text-sm font-semibold text-gray-700">Email</label>
                                                <div className="relative">
                                                    <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                                                    <input
                                                        type="email"
                                                        name="email"
                                                        required
                                                        className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20 outline-none transition-all"
                                                        placeholder="Váš email"
                                                        value={formState.email}
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-sm font-semibold text-gray-700">Telefón</label>
                                                <div className="relative">
                                                    <Phone className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                                                    <input
                                                        type="tel"
                                                        name="phone"
                                                        required
                                                        className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20 outline-none transition-all"
                                                        placeholder="+421 9xx xxx xxx"
                                                        value={formState.phone}
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="w-full bg-brand-orange hover:bg-orange-600 text-white font-bold py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 mt-2 flex items-center justify-center gap-2 disabled:opacity-60"
                                        >
                                            {isSubmitting ? 'Odosielam…' : <>Odoslať dopyt <ArrowRight size={20} /></>}
                                        </button>
                                    </form>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white rounded-3xl p-8 md:p-16 shadow-2xl relative z-10 text-center max-w-3xl mx-auto">
                            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 text-green-600">
                                <CheckCircle2 className="w-10 h-10" />
                            </div>
                            <h2 className="font-heading text-3xl md:text-4xl font-bold text-brand-dark-blue mb-4">
                                Dopyt úspešne odoslaný
                            </h2>
                            <p className="text-lg text-gray-600 mb-8 max-w-xl mx-auto">
                                Ďakujem za dôveru. Ozvem sa vám čo najskôr, aby sme prebrali detaily. Ak to ponáhľa, môžete si rovno vybrať termín v kalendári.
                            </p>

                            <div className="grid md:grid-cols-2 gap-4 max-w-lg mx-auto">
                                <Link
                                    to="/konzultace"
                                    className="flex items-center justify-center gap-2 bg-brand-orange text-white font-bold py-4 px-6 rounded-xl hover:bg-orange-600 transition-colors shadow-lg"
                                >
                                    <Calendar className="w-5 h-5" />
                                    <span>Rezervovať call</span>
                                </Link>
                                <button
                                    onClick={() => window.location.reload()}
                                    className="flex items-center justify-center gap-2 bg-gray-100 text-gray-700 font-bold py-4 px-6 rounded-xl hover:bg-gray-200 transition-colors"
                                >
                                    <span>Nové zadanie</span>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
};

export default CorporateCooperation;
